'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { ChatMessage } from '@/types/chat.types';

// UUID generator
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface LiveChatWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

const CHAT_STORAGE_PREFIX = 'telesky_local_chat_';
const SESSION_STORAGE_KEY = 'telesky_local_chat_session_id';
const NAME_STORAGE_KEY = 'telesky_local_chat_name';

export default function LiveChatWidget({ isOpen, onClose }: LiveChatWidgetProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState('');

  // NOVO:
  const [userName, setUserName] = useState('');
  const [isNameSet, setIsNameSet] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Hydrate + local session init
  useEffect(() => {
    let storedSessionId = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedSessionId) {
      storedSessionId = `session_${generateUUID()}`;
      window.localStorage.setItem(SESSION_STORAGE_KEY, storedSessionId);
    }
    setSessionId(storedSessionId);

    const storedName = window.localStorage.getItem(NAME_STORAGE_KEY);
    if (storedName) {
      setUserName(storedName);
      setIsNameSet(true);
    }

    setIsHydrated(true);
  }, []);

  // Scroll to bottom
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleCloseChat = () => {
    if (sessionId) {
      window.localStorage.removeItem(`${CHAT_STORAGE_PREFIX}${sessionId}`);
    }
    window.localStorage.removeItem(NAME_STORAGE_KEY);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);

    const newSessionId = `session_${generateUUID()}`;
    window.localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);

    setMessages([]);
    setInputValue('');
    setError('');
    setUserName('');
    setIsNameSet(false);
    setSessionId(newSessionId);

    onClose();
  };

  // Load local messages when widget opens
  useEffect(() => {
    if (isOpen && isNameSet && sessionId) {
      try {
        const raw = window.localStorage.getItem(`${CHAT_STORAGE_PREFIX}${sessionId}`);
        const parsed: ChatMessage[] = raw ? JSON.parse(raw) : [];
        setMessages(Array.isArray(parsed) ? parsed : []);
      } catch {
        setMessages([]);
      }
      scrollToBottom();
    }
  }, [isOpen, isNameSet, sessionId]);

  // Persist local messages
  useEffect(() => {
    if (!isHydrated || !sessionId) return;
    window.localStorage.setItem(
      `${CHAT_STORAGE_PREFIX}${sessionId}`,
      JSON.stringify(messages),
    );
  }, [messages, isHydrated, sessionId]);

  // Pull Telegram replies mapped to this session.
  useEffect(() => {
    if (!isHydrated || !isOpen || !isNameSet || !sessionId) return;

    const poll = async () => {
      try {
        const res = await fetch(
          `/api/telegram/replies?sessionId=${encodeURIComponent(sessionId)}&t=${Date.now()}`,
          { cache: 'no-store' },
        );
        if (!res.ok) return;
        const json = await res.json();
        const replies = Array.isArray(json?.data) ? json.data : [];

        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => String(m.id)));
          const incoming: ChatMessage[] = replies
            .filter((r: any) => !existingIds.has(String(r.id)))
            .map((r: any) => ({
              id: String(r.id),
              sessionId,
              senderName: String(r.senderName || 'Telegram agent'),
              messageText: String(r.messageText || ''),
              direction: 'incoming',
              status: 'delivered',
              createdAt: String(r.createdAt || new Date().toISOString()),
              updatedAt: String(r.createdAt || new Date().toISOString()),
            }));

          if (incoming.length === 0) return prev;
          return [...prev, ...incoming];
        });
      } catch {
        // Ignore transient polling errors.
      }
    };

    poll();
    const id = window.setInterval(poll, 3000);
    return () => window.clearInterval(id);
  }, [isHydrated, isOpen, isNameSet, sessionId]);

  // SEND MESSAGE
  const handleSendMessage = async () => {
    if (inputValue.trim() === '' || loading) return;
    let pendingMessageId = '';

    try {
      setLoading(true);
      setError('');
      const cleanText = inputValue.trim();

      const now = new Date().toISOString();
      const localMessage: ChatMessage = {
        id: generateUUID(),
        sessionId,
        senderName: userName,
        senderEmail: `${userName}@widget.chat`,
        messageText: cleanText,
        direction: 'outgoing',
        status: 'pending',
        createdAt: now,
        updatedAt: now,
      };
      pendingMessageId = localMessage.id;

      setMessages((prev) => [...prev, localMessage]);
      setInputValue('');
      scrollToBottom();

      const telegramRes = await fetch('/api/telegram/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          text: `<b>${userName}</b>\n${cleanText}`,
        }),
      });

      if (!telegramRes.ok) {
        throw new Error('Poruka nije poslana na Telegram.');
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === pendingMessageId
            ? { ...msg, status: 'sent', updatedAt: new Date().toISOString() }
            : msg,
        ),
      );
    } catch (err) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === pendingMessageId
            ? { ...msg, status: 'failed', updatedAt: new Date().toISOString() }
            : msg,
        ),
      );
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('sr-RS', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  if (!isHydrated) return null;
  if (!isOpen) return null;

  // -----------------------------------------------------------
  // 1) EKRAN: Unos imena prije chata
  // -----------------------------------------------------------
  if (!isNameSet) {
    return (
      <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:238:6" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="238" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-3%20right-3%20left-3%20sm%3Aleft-auto%20sm%3Abottom-6%20sm%3Aright-6%20z-50%20w-auto%20sm%3Aw-full%20sm%3Amax-w-md%22%7D" className="fixed bottom-3 right-3 left-3 sm:left-auto sm:bottom-6 sm:right-6 z-50 w-auto sm:w-full sm:max-w-md">
        <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:239:8" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="239" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-2xl%20shadow%20p-4%20sm%3Ap-6%22%7D" className="bg-card border border-border rounded-2xl shadow p-4 sm:p-6">
          <h3 data-component-id="src/app/support-center/components/LiveChatWidget.tsx:240:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="240" data-component-file="LiveChatWidget.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20mb-2%22%2C%22textContent%22%3A%22Dobrodo%C5%A1li!%22%7D" className="text-lg font-semibold mb-2">Dobrodošli!</h3>
          <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:241:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="241" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mb-4%22%2C%22textContent%22%3A%22Molimo%20unesite%20va%C5%A1e%20ime%20da%20zapo%C4%8Dnete%20razgovor.%22%7D" className="text-sm text-muted-foreground mb-4">
            Molimo unesite vaše ime da započnete razgovor.
          </p>

          <input data-component-id="src/app/support-center/components/LiveChatWidget.tsx:245:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="245" data-component-file="LiveChatWidget.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AuserName%5D%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20border%20border-border%20rounded-xl%20mb-4%20bg-background%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="Vaše ime"
            className="w-full px-4 py-3 border border-border rounded-xl mb-4 bg-background focus:ring-2 focus:ring-primary"
          />

          <button data-component-id="src/app/support-center/components/LiveChatWidget.tsx:253:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="253" data-component-file="LiveChatWidget.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20py-3%20bg-primary%20text-white%20rounded-xl%20hover%3Abg-opacity-90%20disabled%3Aopacity-50%22%2C%22textContent%22%3A%22Zapo%C4%8Dni%20razgovor%22%7D"
            onClick={() => {
              const trimmed = userName.trim();
              if (trimmed !== '') {
                setUserName(trimmed);
                setIsNameSet(true);
                window.localStorage.setItem(NAME_STORAGE_KEY, trimmed);
              }
            }}
            disabled={userName.trim() === ''}
            className="w-full py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 disabled:opacity-50"
          >
            Započni razgovor
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------
  // 2) GLAVNI CHAT INTERFEJS
  // -----------------------------------------------------------
  return (
    <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:276:4" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="276" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-3%20right-3%20left-3%20sm%3Aleft-auto%20sm%3Abottom-6%20sm%3Aright-6%20z-50%20w-auto%20sm%3Aw-full%20sm%3Amax-w-md%22%7D" className="fixed bottom-3 right-3 left-3 sm:left-auto sm:bottom-6 sm:right-6 z-50 w-auto sm:w-full sm:max-w-md">
      <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:277:6" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="277" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-2xl%20shadow%20overflow-hidden%22%7D" className="bg-card border border-border rounded-2xl shadow overflow-hidden">
        
        {/* HEADER */}
        <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:280:8" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="280" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gradient-to-r%20from-sky-blue%20to-primary%20p-4%20flex%20items-center%20justify-between%22%7D" className="bg-gradient-to-r from-sky-blue to-primary p-4 flex items-center justify-between">
          <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:281:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="281" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <h3 data-component-id="src/app/support-center/components/LiveChatWidget.tsx:282:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="282" data-component-file="LiveChatWidget.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-white%20font-semibold%22%2C%22textContent%22%3A%22Chat%20sa%22%7D" className="text-white font-semibold">Chat sa {userName}</h3>
            <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:283:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="283" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F80%20text-xs%22%2C%22textContent%22%3A%22Lokalni%20chat%20na%20ovom%20ure%C4%91aju%22%7D" className="text-white/80 text-xs">Lokalni chat na ovom uređaju</p>
          </div>

          <button data-component-id="src/app/support-center/components/LiveChatWidget.tsx:286:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="286" data-component-file="LiveChatWidget.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-white%20hover%3Abg-white%2F20%20rounded-lg%20p-2%22%7D"
            onClick={handleCloseChat}
            className="text-white hover:bg-white/20 rounded-lg p-2"
          >
            <Icon data-component-id="src/app/support-center/components/LiveChatWidget.tsx:290:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="290" data-component-file="LiveChatWidget.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* MESSAGES */}
        <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:295:8" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="295" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-%5B60vh%5D%20sm%3Ah-96%20overflow-y-auto%20p-3%20sm%3Ap-4%20space-y-4%20bg-muted%2F20%22%7D" className="h-[60vh] sm:h-96 overflow-y-auto p-3 sm:p-4 space-y-4 bg-muted/20">

          {messages.map((message) => (
            <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:298:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="298" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={message.id}
              className={`flex ${
                message.direction === 'incoming'
                  ? 'justify-start'
                  : 'justify-end'
              }`}
            >
              <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:306:14" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="306" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.direction === 'incoming'
                    ? 'bg-card border border-border'
                    : 'bg-primary text-white'
                }`}
              >
                <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:313:16" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="313" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20font-semibold%20opacity-70%20mb-1%22%7D" className="text-xs font-semibold opacity-70 mb-1">
                  {message.senderName}
                </p>
                <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:316:16" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="316" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%22%7D" className="text-sm">{message.messageText}</p>
                <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:317:16" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="317" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20opacity-60%20mt-1%22%7D" className="text-xs opacity-60 mt-1">
                  {formatTime(message.createdAt)}
                </p>
              </div>
            </div>
          ))}

          <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:324:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="324" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" ref={messagesEndRef} />
        </div>

        {/* ERROR */}
        {error && (
          <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:329:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="329" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20py-2%20bg-red-50%20border-t%20border-red-100%22%7D" className="px-4 py-2 bg-red-50 border-t border-red-100">
            <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:330:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="330" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-red-600%22%7D" className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* INPUT */}
        <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:335:8" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="335" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20sm%3Ap-4%20border-t%20border-border%20bg-card%22%7D" className="p-3 sm:p-4 border-t border-border bg-card">
          <div data-component-id="src/app/support-center/components/LiveChatWidget.tsx:336:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="336" data-component-file="LiveChatWidget.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-end%20space-x-2%22%7D" className="flex items-end space-x-2">
            <textarea data-component-id="src/app/support-center/components/LiveChatWidget.tsx:337:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="337" data-component-file="LiveChatWidget.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22value%22%3A%22%5Bvar%3AinputValue%5D%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20border%20border-border%20rounded-xl%20resize-none%20focus%3Aring-2%20focus%3Aring-primary%20bg-background%22%7D"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Unesite vašu poruku..."
              rows={2}
              disabled={loading}
              className="flex-1 px-4 py-3 border border-border rounded-xl resize-none focus:ring-2 focus:ring-primary bg-background"
            />
            <button data-component-id="src/app/support-center/components/LiveChatWidget.tsx:346:12" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="346" data-component-file="LiveChatWidget.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-3%20bg-primary%20text-white%20rounded-xl%20hover%3Abg-opacity-90%20disabled%3Aopacity-50%22%7D"
              onClick={handleSendMessage}
              disabled={inputValue.trim() === '' || loading}
              className="px-4 py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 disabled:opacity-50"
            >
              {loading ? (
                <Icon data-component-id="src/app/support-center/components/LiveChatWidget.tsx:352:16" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="352" data-component-file="LiveChatWidget.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%2C%22className%22%3A%22animate-spin%22%7D" name="ArrowPathIcon" size={20} className="animate-spin" />
              ) : (
                <Icon data-component-id="src/app/support-center/components/LiveChatWidget.tsx:354:16" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="354" data-component-file="LiveChatWidget.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PaperAirplaneIcon%22%7D" name="PaperAirplaneIcon" size={20} />
              )}
            </button>
          </div>

          <p data-component-id="src/app/support-center/components/LiveChatWidget.tsx:359:10" data-component-path="src/app/support-center/components/LiveChatWidget.tsx" data-component-line="359" data-component-file="LiveChatWidget.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20mt-2%22%2C%22textContent%22%3A%22Poruke%20se%20%C4%8Duvaju%20lokalno%20i%20%C5%A1alju%20direktno%20na%20Telegram%20podr%C5%A1ke%20TELESKY.%22%7D" className="text-xs text-muted-foreground mt-2">
            Poruke se čuvaju lokalno i šalju direktno na Telegram podrške TELESKY.
          </p>
        </div>
      </div>
    </div>
  );
}
